import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Link, useLocation } from 'react-router-dom';
import { ChevronDownIcon, ChevronRightIcon } from '@heroicons/react/24/outline';
import { useUser } from '../store/UserContext';
import { t } from '../i18n';
import { createMenuItems, hasMenuPermission, isMenuActive } from '../config/navMenu';
import { matchWeatherAgronomyModule } from '../config/weatherAgronomyModules';
import { matchSystemManagementModule, systemManagementModules } from '../config/systemManagementModules';
import { matchProfileModule } from '../config/profileModules';
import { withWeatherAgronomySearch } from '../utils/weatherAgronomyContext';
import {
  mergeBusinessContextSearch,
  preserveBusinessContextTo,
  readBusinessContext,
  withBusinessContextSearch,
} from '../utils/businessContextNav';

function splitMenuPath(path) {
  if (!path) return { pathname: '', search: '' };
  const q = path.indexOf('?');
  if (q < 0) return { pathname: path, search: '' };
  return { pathname: path.slice(0, q), search: path.slice(q + 1) };
}

function isWeatherAgronomyPath(path) {
  const p = String(path || '');
  return p === '/weather-agronomy' || p.startsWith('/weather-agronomy/');
}

function needsBusinessContextPreserve(pathname) {
  const p = String(pathname || '');
  return (
    p === '/welcome' ||
    p.startsWith('/smart-farm-cockpit') ||
    p.startsWith('/production') ||
    isWeatherAgronomyPath(p)
  );
}

/** 气象农艺跳转：一级落到气象数据；保留 WA 坐标键，并叠 deviceId 等业务三键 */
function preserveWeatherAgronomyTo(path, location) {
  const { pathname } = splitMenuPath(path);
  if (!pathname || !isWeatherAgronomyPath(pathname)) return path;
  const target = pathname === '/weather-agronomy' ? '/weather-agronomy/weather-data' : pathname;
  if (isWeatherAgronomyPath(location?.pathname) || needsBusinessContextPreserve(location?.pathname)) {
    const wa = withWeatherAgronomySearch(target, location.search || '');
    // WA 不含 deviceId：再叠业务三键，避免欢迎/生产→气象丢 deviceId
    return withBusinessContextSearch(wa, location.search || '');
  }
  return target;
}

/**
 * 菜单跳转统一透传 farmId/landId/deviceId，避免链路断参。
 * 气象域仍走 WA 工具（含坐标）；其余业务域走 businessContext。
 */
function preserveMenuTo(path, location) {
  const { pathname, search: menuSearch } = splitMenuPath(path);
  if (!pathname) return path;
  if (isWeatherAgronomyPath(pathname)) {
    return preserveWeatherAgronomyTo(path, location);
  }
  if (!needsBusinessContextPreserve(pathname)) return path;
  const ctx = readBusinessContext(new URLSearchParams(location?.search || ''));
  if (menuSearch) {
    const next = mergeBusinessContextSearch(menuSearch, ctx);
    return next ? `${pathname}?${next}` : pathname;
  }
  return preserveBusinessContextTo(pathname, location);
}

function isPathActive(path, pathname, search = '') {
  const { pathname: menuPath, search: menuSearch } = splitMenuPath(path);
  if (!menuPath) return false;
  if (menuSearch) {
    if (pathname !== menuPath) return false;
    const want = new URLSearchParams(menuSearch);
    const have = new URLSearchParams(search.startsWith('?') ? search.slice(1) : search);
    for (const [k, v] of want.entries()) {
      if (have.get(k) !== v) return false;
    }
    return true;
  }
  return pathname === menuPath || pathname.startsWith(`${menuPath}/`);
}

function isNavChildActive(parentPath, child, pathname, search = '') {
  const grand = child.children || [];
  if (grand.length > 0) {
    if (parentPath === '/weather-agronomy') {
      return grand.some((g) => matchWeatherAgronomyModule({ path: splitMenuPath(g.path).pathname }, pathname))
        || matchWeatherAgronomyModule({ path: splitMenuPath(child.path).pathname }, pathname);
    }
    return grand.some((g) => isPathActive(g.path, pathname, search))
      || isPathActive(child.path, pathname, search);
  }
  if (parentPath === '/weather-agronomy') {
    return matchWeatherAgronomyModule({ path: splitMenuPath(child.path).pathname }, pathname);
  }
  if (parentPath === '/system-management') {
    const mod = systemManagementModules.find((m) => m.path === child.path);
    return mod ? matchSystemManagementModule(mod, pathname) : isPathActive(child.path, pathname, search);
  }
  if (parentPath === '/profile') {
    return matchProfileModule({ path: child.path }, pathname);
  }
  return isPathActive(child.path, pathname, search);
}

function hasChildNodes(node) {
  return Array.isArray(node?.children) && node.children.length > 0;
}

function HorizontalNavDropdown({
  item,
  permitted,
  barClass,
  stateClass,
  disabledClass,
  setMobileOpen,
  isDrawer,
  onDropdownOpenChange
}) {
  const [open, setOpen] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const [expandedChildPath, setExpandedChildPath] = useState(null);
  const [hoverChildPath, setHoverChildPath] = useState(null);
  const triggerRef = useRef(null);
  const closeTimerRef = useRef(null);
  const level3TimerRef = useRef(null);
  const rowRefs = useRef({});
  const [panelPos, setPanelPos] = useState({ top: 0, left: 0, width: 176 });
  const [level3Pos, setLevel3Pos] = useState({ top: 0, left: 0, width: 168 });
  const location = useLocation();
  const children = item.children || [];

  const clearCloseTimer = () => {
    if (closeTimerRef.current) {
      clearTimeout(closeTimerRef.current);
      closeTimerRef.current = null;
    }
  };

  const clearLevel3Timer = () => {
    if (level3TimerRef.current) {
      clearTimeout(level3TimerRef.current);
      level3TimerRef.current = null;
    }
  };

  const updatePanelPos = () => {
    const rect = triggerRef.current?.getBoundingClientRect();
    if (!rect) return;
    setPanelPos({ top: rect.bottom + 6, left: rect.left + rect.width / 2, width: Math.max(rect.width, 176) });
  };

  const updateLevel3Pos = (childPath) => {
    const row = rowRefs.current[childPath];
    if (!row) return;
    const rect = row.getBoundingClientRect();
    const width = 168;
    const gap = 4;
    const preferLeft = rect.right + gap;
    const left = preferLeft + width > window.innerWidth - 8
      ? Math.max(8, rect.left - gap - width)
      : preferLeft;
    setLevel3Pos({
      top: Math.min(rect.top, window.innerHeight - 48),
      left,
      width
    });
  };

  const openDropdown = () => {
    if (!permitted) return;
    clearCloseTimer();
    updatePanelPos();
    setOpen(true);
  };

  const scheduleCloseDropdown = () => {
    clearCloseTimer();
    closeTimerRef.current = setTimeout(() => {
      setOpen(false);
      setHoverChildPath(null);
      closeTimerRef.current = null;
    }, 140);
  };

  const openLevel3 = (child) => {
    if (!hasChildNodes(child)) {
      clearLevel3Timer();
      setHoverChildPath(null);
      return;
    }
    clearLevel3Timer();
    updateLevel3Pos(child.path);
    setHoverChildPath(child.path);
  };

  const scheduleCloseLevel3 = () => {
    clearLevel3Timer();
    level3TimerRef.current = setTimeout(() => {
      setHoverChildPath(null);
      level3TimerRef.current = null;
    }, 140);
  };

  useEffect(() => {
    setOpen(false);
    setExpanded(false);
    setExpandedChildPath(null);
    setHoverChildPath(null);
  }, [location.pathname, location.search]);

  useEffect(() => () => {
    clearCloseTimer();
    clearLevel3Timer();
  }, []);

  useEffect(() => {
    if (!onDropdownOpenChange) return undefined;
    onDropdownOpenChange(open);
    return () => {
      if (open) onDropdownOpenChange(false);
    };
  }, [open, onDropdownOpenChange]);

  useEffect(() => {
    if (!open) return undefined;
    updatePanelPos();
    if (hoverChildPath) updateLevel3Pos(hoverChildPath);
    const onScrollOrResize = () => {
      updatePanelPos();
      if (hoverChildPath) updateLevel3Pos(hoverChildPath);
    };
    window.addEventListener('resize', onScrollOrResize);
    window.addEventListener('scroll', onScrollOrResize, true);
    return () => {
      window.removeEventListener('resize', onScrollOrResize);
      window.removeEventListener('scroll', onScrollOrResize, true);
    };
  }, [open, hoverChildPath]);

  const triggerClass = `${barClass} ${permitted ? stateClass : disabledClass} gap-1`;

  const closeAll = () => {
    setOpen(false);
    setExpanded(false);
    setExpandedChildPath(null);
    setHoverChildPath(null);
    setMobileOpen?.(false);
  };

  const linkClass = (active, inDrawer = false) =>
    inDrawer
      ? `flex w-full items-center justify-between gap-2 rounded-md px-3 py-2.5 text-sm text-left transition-colors ${
          active
            ? 'bg-blue-50 text-blue-700 font-medium dark:bg-blue-950/40 dark:text-blue-300'
            : 'text-gray-700 hover:bg-gray-50 dark:text-gray-300 dark:hover:bg-gray-700/50'
        }`
      : `flex w-full items-center justify-between gap-2 px-4 py-2.5 text-sm text-left transition-colors ${
          active
            ? 'bg-blue-50 text-blue-700 font-medium dark:bg-blue-950/40 dark:text-blue-300'
            : 'text-gray-700 hover:bg-blue-50/80 dark:text-gray-300 dark:hover:bg-gray-700/50'
        }`;

  const renderLeafLink = (node, inDrawer = false) => {
    const active = item.path === '/weather-agronomy'
      ? matchWeatherAgronomyModule({ path: splitMenuPath(node.path).pathname }, location.pathname)
      : isPathActive(node.path, location.pathname, location.search);
    const Icon = node.icon;
    return (
      <Link
        key={node.path}
        to={preserveMenuTo(node.path, location)}
        className={linkClass(active, inDrawer)}
        onClick={closeAll}
      >
        <span className="flex min-w-0 items-center gap-2">
          {Icon ? <Icon className="h-4 w-4 shrink-0 opacity-80" /> : null}
          <span className="truncate">{node.title}</span>
        </span>
      </Link>
    );
  };

  const renderDrawerChild = (child) => {
    const childActive = isNavChildActive(item.path, child, location.pathname, location.search);
    const Icon = child.icon;
    const nested = hasChildNodes(child);
    const isOpen = expandedChildPath === child.path;

    if (!nested) {
      return renderLeafLink(child, true);
    }

    return (
      <div key={child.path} className="flex flex-col">
        <div className="flex items-stretch gap-0.5">
          <Link
            to={preserveMenuTo(child.path, location)}
            className={`${linkClass(childActive, true)} flex-1`}
            onClick={closeAll}
          >
            <span className="flex min-w-0 items-center gap-2">
              {Icon ? <Icon className="h-4 w-4 shrink-0 opacity-80" /> : null}
              <span className="truncate">{child.title}</span>
            </span>
          </Link>
          <button
            type="button"
            className="shrink-0 rounded-md px-2 text-gray-500 hover:bg-gray-50 dark:hover:bg-gray-700/50"
            aria-expanded={isOpen}
            aria-label={isOpen ? '收起子菜单' : '展开子菜单'}
            onClick={() => setExpandedChildPath((prev) => (prev === child.path ? null : child.path))}
          >
            <ChevronDownIcon className={`h-3.5 w-3.5 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
          </button>
        </div>
        {isOpen && (
          <div className="ml-2 mt-0.5 flex flex-col gap-0.5 border-l border-gray-200 pl-2 dark:border-gray-600">
            {child.children.map((g) => renderLeafLink(g, true))}
          </div>
        )}
      </div>
    );
  };

  if (isDrawer) {
    return (
      <div key={item.path} className="flex flex-col">
        <button
          type="button"
          disabled={!permitted}
          onClick={() => permitted && setExpanded((prev) => !prev)}
          className={`${triggerClass} w-full justify-between`}
        >
          <span className="flex items-center gap-2">
            {item.icon ? <item.icon className="h-4 w-4 shrink-0" /> : null}
            <span>{item.title}</span>
          </span>
          <ChevronDownIcon className={`h-4 w-4 shrink-0 transition-transform ${expanded ? 'rotate-180' : ''}`} />
        </button>
        {expanded && permitted && (
          <div className="ml-2 mt-1 flex flex-col gap-0.5 border-l border-gray-200 pl-2 dark:border-gray-600">
            {children.map((child) => renderDrawerChild(child))}
          </div>
        )}
      </div>
    );
  }

  const hoverChild = children.find((c) => c.path === hoverChildPath && hasChildNodes(c));

  const level3Panel = open && permitted && hoverChild && createPortal(
    <div
      className="fixed z-[110] overflow-hidden rounded-xl border border-gray-200/90 bg-white py-1.5 shadow-xl ring-1 ring-black/5 dark:border-gray-600 dark:bg-gray-800 dark:ring-white/10"
      style={{
        top: level3Pos.top,
        left: level3Pos.left,
        minWidth: level3Pos.width
      }}
      role="menu"
      onMouseEnter={() => {
        clearCloseTimer();
        clearLevel3Timer();
        setHoverChildPath(hoverChild.path);
      }}
      onMouseLeave={() => {
        scheduleCloseLevel3();
        scheduleCloseDropdown();
      }}
    >
      {hoverChild.children.map((g, index) => (
        <React.Fragment key={g.path}>
          {index > 0 ? <div className="mx-3 border-t border-gray-100 dark:border-gray-700" /> : null}
          {renderLeafLink(g)}
        </React.Fragment>
      ))}
    </div>,
    document.body
  );

  const dropdownPanel = open && permitted && createPortal(
    <div
      className="fixed z-[100] overflow-visible rounded-xl border border-gray-200/90 bg-white py-1.5 shadow-xl ring-1 ring-black/5 dark:border-gray-600 dark:bg-gray-800 dark:ring-white/10"
      style={{
        top: panelPos.top,
        left: panelPos.left,
        minWidth: panelPos.width,
        transform: 'translateX(-50%)'
      }}
      role="menu"
      onMouseEnter={openDropdown}
      onMouseLeave={scheduleCloseDropdown}
    >
      {children.map((child, index) => {
        const childActive = isNavChildActive(item.path, child, location.pathname, location.search);
        const Icon = child.icon;
        const nested = hasChildNodes(child);
        const rowActive = hoverChildPath === child.path;

        return (
          <React.Fragment key={child.path}>
            {index > 0 ? <div className="mx-3 border-t border-gray-100 dark:border-gray-700" /> : null}
            <div
              ref={(el) => {
                if (el) rowRefs.current[child.path] = el;
                else delete rowRefs.current[child.path];
              }}
              onMouseEnter={() => openLevel3(child)}
              onMouseLeave={() => {
                if (nested) scheduleCloseLevel3();
              }}
            >
              {nested ? (
                <Link
                  to={preserveMenuTo(child.path, location)}
                  className={linkClass(childActive || rowActive)}
                  onClick={closeAll}
                >
                  <span className="flex min-w-0 items-center gap-2">
                    {Icon ? <Icon className="h-4 w-4 shrink-0 opacity-80" /> : null}
                    <span className="truncate">{child.title}</span>
                  </span>
                  <ChevronRightIcon className="h-3.5 w-3.5 shrink-0 opacity-70" />
                </Link>
              ) : (
                renderLeafLink(child)
              )}
            </div>
          </React.Fragment>
        );
      })}
    </div>,
    document.body
  );

  if (!permitted) {
    return (
      <div key={item.path} className="relative inline-flex h-full items-center shrink-0">
        <span className={triggerClass} title="无权限">
          <span>{item.title}</span>
        </span>
      </div>
    );
  }

  return (
    <div
      key={item.path}
      className="relative inline-flex h-full items-center shrink-0"
      onMouseEnter={openDropdown}
      onMouseLeave={scheduleCloseDropdown}
    >
      <Link
        ref={triggerRef}
        to={preserveMenuTo(item.path, location)}
        aria-expanded={open}
        aria-haspopup="menu"
        className={triggerClass}
        title={item.title}
        onClick={() => {
          setOpen(false);
          setHoverChildPath(null);
          setMobileOpen?.(false);
        }}
      >
        <span>{item.title}</span>
      </Link>
      {dropdownPanel}
      {level3Panel}
    </div>
  );
}

function HorizontalNav({ mobileOpen, setMobileOpen, variant = 'bar', onDropdownOpenChange }) {
  const location = useLocation();
  const { user } = useUser();

  if (!user) return null;

  const menuItems = createMenuItems(user, t);
  const isDrawer = variant === 'drawer';

  const barClass = [
    'inline-flex shrink-0 items-center justify-center',
    'h-full px-4 min-[520px]:px-5 py-2',
    'text-sm min-[520px]:text-[15px] leading-normal font-medium whitespace-nowrap',
    'transition-colors duration-200',
    'relative after:content-[""] after:absolute after:bottom-0 after:inset-x-3 min-[520px]:after:inset-x-4 after:h-[3px] after:rounded-sm after:transition-colors'
  ].join(' ');

  const drawerClass = 'flex w-full items-center gap-2 px-3 py-2.5 rounded-lg text-sm font-medium transition-all duration-200';

  const renderMenuItem = (item) => {
    const permitted = hasMenuPermission(item, user);
    const active = isMenuActive(item, location.pathname);
    const hasChildren = Array.isArray(item.children) && item.children.length > 0;

    if (hasChildren) {
      const stateClass = isDrawer
        ? (active
          ? 'bg-gradient-to-r from-blue-500 to-blue-600 text-white shadow-sm'
          : 'text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700')
        : (active
          ? 'text-blue-600 dark:text-blue-400 font-semibold after:bg-blue-500 dark:after:bg-blue-400'
          : 'text-gray-600 dark:text-gray-300 hover:text-blue-600 dark:hover:text-blue-400 after:bg-transparent hover:bg-gray-50/80 dark:hover:bg-gray-700/30');

      return (
        <HorizontalNavDropdown
          key={item.path}
          item={item}
          permitted={permitted}
          barClass={isDrawer ? drawerClass : barClass}
          stateClass={stateClass}
          disabledClass="cursor-not-allowed opacity-40 text-gray-400"
          setMobileOpen={setMobileOpen}
          isDrawer={isDrawer}
          onDropdownOpenChange={onDropdownOpenChange}
        />
      );
    }

    const baseClass = isDrawer ? drawerClass : barClass;
    const stateClass = isDrawer
      ? (active
        ? 'bg-gradient-to-r from-blue-500 to-blue-600 text-white shadow-sm'
        : 'text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700')
      : (active
        ? 'text-blue-600 dark:text-blue-400 font-semibold after:bg-blue-500 dark:after:bg-blue-400'
        : 'text-gray-600 dark:text-gray-300 hover:text-blue-600 dark:hover:text-blue-400 after:bg-transparent hover:bg-gray-50/80 dark:hover:bg-gray-700/30');
    const disabledClass = 'cursor-not-allowed opacity-40 text-gray-400';

    const content = isDrawer ? (
      <>
        {item.icon && <item.icon className={`h-4 w-4 shrink-0 ${active ? 'text-white' : ''}`} />}
        <span>{item.title}</span>
      </>
    ) : (
      <span>{item.title}</span>
    );

    if (!permitted) {
      return (
        <span key={item.path} className={`${baseClass} ${disabledClass}`} title="无权限">
          {content}
        </span>
      );
    }

    if (item.openInNewTab) {
      return (
        <a
          key={item.path}
          href={item.path}
          target="_blank"
          rel="noopener noreferrer"
          className={`${baseClass} ${stateClass}`}
          onClick={() => setMobileOpen?.(false)}
        >
          {content}
        </a>
      );
    }

    return (
      <Link
        key={item.path}
        to={preserveMenuTo(item.path, location)}
        className={`${baseClass} ${stateClass}`}
        onClick={() => setMobileOpen?.(false)}
      >
        {content}
      </Link>
    );
  };

  if (isDrawer) {
    return (
      <nav className="px-2 py-2">
        <div className="flex flex-col gap-1">
          {menuItems.map(renderMenuItem)}
        </div>
      </nav>
    );
  }

  return (
    <nav className="min-w-0 w-full h-full flex items-center justify-center">
      <div className="flex items-center justify-center h-full gap-x-1 sm:gap-x-2 min-w-0 w-full flex-nowrap overflow-x-auto [scrollbar-width:thin]">
        {menuItems.map(renderMenuItem)}
      </div>
    </nav>
  );
}

export default HorizontalNav;
